import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import {DisabledSection} from 'shells/disabled-section/disabled-section';
import {FlashBanner} from 'shells/flash-banner/flash-banner';
import {Link} from 'shells/link/link';
import {SpacerGroup} from 'shells/spacer/spacer-group';
import {MarketingTitle} from 'shells/typography/marketing-title';

import type {UserEmail as UserEmailFragment} from 'nutshell-graphql-types';

import {NotifyMessages} from '../../../core/notify-messages/notify-messages';

import {EmailSignatureContainer} from '../email-signature-container';

import {EmailAddressClickActionSetting} from './email-address-click-action-setting';
import {EmailTrackingSetting} from './email-tracking-setting';
import {EmailWidget} from './email-widget';

import styles from './email-sending-settings.module.css';

type Props = {
    isLoading: boolean;
    errorMessage: string | null | undefined;
    isVerificationPollTimedOut: boolean;
    configuredUserEmails: UserEmailFragment[];
    refetch: () => Promise<any>;
};

export function EmailSendingSettings(props: Props) {
    const [errorMessages, setErrorMessages] = React.useState<string[]>(
        props.errorMessage ? [props.errorMessage] : []
    );
    const [successMessages, setSuccessMessages] = React.useState<React.ReactElement<any>[]>([]);

    return (
        <div>
            {errorMessages.map((message) => (
                <FlashBanner
                    key={message}
                    hasBorder={true}
                    type='error'
                    onHidden={() => {
                        setErrorMessages(errorMessages.filter((msg) => msg !== message));
                    }}
                >
                    {message}
                </FlashBanner>
            ))}
            <NotifyMessages />
            {successMessages.map((message, index) => (
                <FlashBanner
                    key={index}
                    hasBorder={true}
                    type='success'
                    onHidden={() => {
                        setSuccessMessages(successMessages.filter((msg) => msg !== message));
                    }}
                >
                    {message}
                </FlashBanner>
            ))}
            <DisabledSection disabled={props.isLoading}>
                <SpacerGroup mb={32}>
                    <MarketingTitle>
                        {props.configuredUserEmails.length > 1 ? 'Accounts' : 'Account'}
                    </MarketingTitle>
                    <div className={styles['email-widget-container']}>
                        {props.configuredUserEmails.map((email) => {
                            return (
                                <EmailWidget
                                    email={email}
                                    key={email.id}
                                    isVerificationPollTimedOut={props.isVerificationPollTimedOut}
                                    retryVerification={props.refetch}
                                    onAuthStart={() => {
                                        setErrorMessages([]);
                                    }}
                                    onAuthSuccess={() => {
                                        return props.refetch();
                                    }}
                                    onAuthError={(errMsg) => {
                                        setErrorMessages(errorMessages.concat(errMsg));
                                    }}
                                />
                            );
                        })}
                    </div>
                    <div className={styles['link-container']}>
                        <Link
                            // @ts-expect-error
                            as={RoutedLink}
                            variant='primary'
                            to={{
                                hash: hasGoogleOrOffice(props.configuredUserEmails)
                                    ? '/connect-email/configure-smtp'
                                    : '/connect-email/choose-provider',
                            }}
                        >
                            + Add another account
                        </Link>
                    </div>
                </SpacerGroup>
            </DisabledSection>
            <DisabledSection disabled={props.isLoading}>
                <SpacerGroup mb={32}>
                    <MarketingTitle>
                        When you click an email address in Nutshell do you want to:
                    </MarketingTitle>
                    <EmailAddressClickActionSetting
                        onError={(errorMessage) => {
                            setErrorMessages(errorMessages.concat(errorMessage));
                        }}
                        disabled={props.isLoading}
                    />
                </SpacerGroup>

                <SpacerGroup mb={32}>
                    <MarketingTitle>Email tracking</MarketingTitle>
                    <EmailTrackingSetting
                        onError={(errorMessage) => {
                            setErrorMessages(errorMessages.concat(errorMessage));
                        }}
                        disabled={props.isLoading}
                    />
                </SpacerGroup>

                <MarketingTitle>Email signature</MarketingTitle>
                <EmailSignatureContainer disabled={props.isLoading} />
            </DisabledSection>
        </div>
    );
}

function hasGoogleOrOffice(emails: UserEmailFragment[]): boolean {
    return (
        emails.filter((email) => ['GOOGLE', 'GOOGLE_APPS', 'OFFICE365'].includes(email.type))
            .length > 0
    );
}
